import { type FunctionComponent, type SVGProps } from "react";
import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  CheckCircleIcon,
} from "vega-icons";

import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { NdaTrackingStatus } from "@vega-api/generated/types/ndaTrackingStatus";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

// NDA status is optional on the breakdown; an unset value means the NDA is still
// with the LP (the start of the workflow).
export const resolveNdaStatus = (
  row?: Pick<DealTrackingLimitedPartnerBreakdown, "ndaTrackingStatus">,
): NdaTrackingStatus => row?.ndaTrackingStatus ?? NdaTrackingStatus.WITH_LP;

// Whether an NDA actually exists for this LP. With none issued there is nothing to
// sign or track, so the NDA cell stays empty and inert (see VD-28374).
export const hasIssuedNda = (
  row?: Pick<DealTrackingLimitedPartnerBreakdown, "numberOfNdasIssued">,
): boolean => (row?.numberOfNdasIssued ?? 0) > 0;

type IconComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

// The three NDA workflow states share one filled circular glyph each, tinted to
// match the platform status palette: With LP (blue/pending), With GP
// (orange/warning), Signed (green/positive).
const STATUS_ICON: Record<
  NdaTrackingStatus,
  { Icon: IconComponent; className: string }
> = {
  [NdaTrackingStatus.WITH_LP]: {
    Icon: ArrowRightCircleIcon,
    className: "text-text/pending",
  },
  [NdaTrackingStatus.WITH_GP]: {
    Icon: ArrowLeftCircleIcon,
    className: "text-text/warning",
  },
  [NdaTrackingStatus.SIGNED]: {
    Icon: CheckCircleIcon,
    className: "text-text/positive",
  },
};

export const NDA_STATUS_LABEL: Record<NdaTrackingStatus, string> = {
  [NdaTrackingStatus.WITH_LP]: m.coInvestments_nda_status_withLp(),
  [NdaTrackingStatus.WITH_GP]: m.coInvestments_nda_status_withGp(),
  [NdaTrackingStatus.SIGNED]: m.coInvestments_nda_status_signed(),
};

export const NDA_STATUS_ORDER: NdaTrackingStatus[] = [
  NdaTrackingStatus.WITH_LP,
  NdaTrackingStatus.WITH_GP,
  NdaTrackingStatus.SIGNED,
];

export const NdaStatusContent = ({ status }: { status: NdaTrackingStatus }) => {
  const { Icon, className } = STATUS_ICON[status];

  return (
    <span className="inline-flex items-center gap-2 body-base-regular">
      <Icon className={cnTwMerge("size-5 shrink-0", className)} />
      {NDA_STATUS_LABEL[status]}
    </span>
  );
};
